// usePartDrag — dragging one part of a container by its grip to another place
// in the reading order (a container card on the canvas, an open binder or
// shelf on the desk). View-ephemeral: only the drag in progress is held;
// releasing it somewhere new goes out as one move (partOps.ts).

export type { PartDragHandlers };
export { usePartDrag };

import { type PointerEvent, type RefObject, useState } from "react";
import { reorderTarget } from "./partOps.js";

// A part being dragged: where it was, and where it would land.
type PartDrag = {
    readonly part: string;
    readonly from: number;
    readonly over: number;
};

type PartDragHandlers = {
    readonly drag: PartDrag | null;
    // The grip's handlers for the part at `from`.
    readonly start: (
        part: string,
        from: number,
    ) => (e: PointerEvent<HTMLElement>) => void;
    readonly move: (e: PointerEvent<HTMLElement>) => void;
    readonly end: (e?: PointerEvent<HTMLElement>) => void;
    readonly cancel: () => void;
    // Where the dragged part would land relative to row `i`, if at all.
    readonly landingAt: (i: number) => "before" | "after" | undefined;
};

const usePartDrag = ({
    list,
    rows,
    enabled,
    onMove,
}: {
    // The list holding the rows, and the selector of its direct rows.
    readonly list: RefObject<HTMLElement | null>;
    readonly rows: string;
    readonly enabled: boolean;
    readonly onMove: (part: string, from: number, to: number) => void;
}): PartDragHandlers => {
    const [drag, setDrag] = useState<PartDrag | null>(null);
    // The rows' vertical middles, as drawn (the canvas may be zoomed; the
    // pointer and the rows are both in page pixels).
    const middles = (): readonly number[] =>
        [...(list.current?.querySelectorAll<HTMLElement>(rows) ?? [])].map(
            (el) => {
                const box = el.getBoundingClientRect();
                return box.top + box.height / 2;
            },
        );
    return {
        drag,
        start: (part, from) => (e) => {
            e.stopPropagation();
            if (e.button !== 0 || !enabled) return;
            e.currentTarget.setPointerCapture(e.pointerId);
            setDrag({ part, from, over: from });
        },
        move: (e) => {
            if (drag === null) return;
            const over = reorderTarget(middles(), drag.from, e.clientY);
            if (over !== drag.over) setDrag({ ...drag, over });
        },
        end: () => {
            if (drag === null) return;
            setDrag(null);
            if (drag.over !== drag.from)
                onMove(drag.part, drag.from, drag.over);
        },
        cancel: () => setDrag(null),
        landingAt: (i) =>
            drag !== null && drag.over !== drag.from && drag.over === i
                ? drag.over < drag.from
                    ? "before"
                    : "after"
                : undefined,
    };
};
